import React, { useEffect, useState } from 'react';
import { adminProductsAPI, clearAdminCredentials, productsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const emptyProduct = {
  name: '',
  category: 'battery',
  image: '',
  images: [],
  specs: [],
  description: '',
  price: 'Sur devis',
  available: true,
};

const toArray = (val) => (val || '').split('\n').map(s => s.trim()).filter(Boolean);

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [galleryItems, setGalleryItems] = useState([]);
  const [pickingMode, setPickingMode] = useState('main'); // 'main' or 'multi'

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      let data = await adminProductsAPI.getAll();
      if (!Array.isArray(data)) data = [];
      // Fallback to public products if admin list is empty (still lets you edit available ones)
      if (data.length === 0) {
        const pub = await productsAPI.getAll();
        data = Array.isArray(pub) ? pub : [];
      }
      setProducts(data);
    } catch (e) {
      setError('Erreur de chargement. Vérifiez vos identifiants admin.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openGallery = async (mode = 'main') => {
    try {
      setPickingMode(mode);
      setGalleryOpen(true);
      setLoading(true);
      const items = await adminProductsAPI.listUploads();
      setGalleryItems(items);
    } catch (e) {
      setError('Impossible de charger la galerie');
    } finally {
      setLoading(false);
    }
  };

  const pickFromGallery = (url) => {
    if (pickingMode === 'main') {
      setForm(f => ({ ...f, image: url }));
      setGalleryOpen(false);
    } else {
      const current = typeof form.images === 'string' ? form.images : (form.images || []).join('\n');
      const next = (current ? (current + '\n' + url) : url);
      setForm(f => ({ ...f, images: next }));
    }
  };

  const uploadMain = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image trop volumineuse (max 5 Mo)');
      return;
    }
    
    try {
      setLoading(true);
      
      // Convertir en Base64 directement
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result;
        setForm(f => ({ 
          ...f, 
          image_base64: base64String,
          image: "" // Vider l'ancien champ URL
        }));
        setLoading(false);
      };
      
      reader.onerror = () => {
        setError('Erreur lors de la lecture de l\'image');
        setLoading(false);
      };
      
      reader.readAsDataURL(file);
      
    } catch (err) {
      setError('Erreur lors de l\'upload de l\'image principale');
      console.error('Upload error:', err);
      setLoading(false);
    }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Vérifier la taille de chaque fichier
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`Image ${file.name} trop volumineuse (max 5 Mo)`);
        return;
      }
    }
    
    try {
      setLoading(true);
      const uploaded = [];
      
      // Convertir chaque fichier en Base64
      for (const file of files) {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            uploaded.push(event.target.result);
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      
      if (uploaded.length > 0) {
        // Handle both string and array format for gallery images Base64
        let currentImages = [];
        if (Array.isArray(form.images_base64)) {
          currentImages = form.images_base64.filter(Boolean);
        } else if (form.images_base64) {
          currentImages = [form.images_base64];
        }
        
        const allImages = [...currentImages, ...uploaded];
        setForm(f => ({ 
          ...f, 
          images_base64: allImages,
          images: [] // Vider l'ancien champ URLs
        }));
      }
    } catch (err) {
      setError('Erreur lors de l\'upload des images de galerie');
      console.error('Gallery upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const payload = {
        ...form,
        specs: Array.isArray(form.specs) ? form.specs : toArray(form.specs),
        images: Array.isArray(form.images) ? form.images : toArray(form.images),
        images_base64: form.images_base64 || [],
        image_base64: form.image_base64 || ""
      };
      if (editingId) {
        await adminProductsAPI.update(editingId, payload);
      } else {
        await adminProductsAPI.create(payload);
      }
      setForm(emptyProduct);
      setEditingId(null);
      await load();
    } catch (err) {
      setError("Échec de l'enregistrement. Vérifiez les champs.");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      ...p,
      specs: (p.specs || []).join('\n'),
      images: (p.images || []).join('\n'),
      images_base64: p.images_base64 || [],
      image_base64: p.image_base64 || ""
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (id) => {
    if (!window.confirm('Marquer ce produit comme non disponible ?')) return;
    try {
      await adminProductsAPI.remove(id);
      await load();
    } catch (e) {
      setError('Suppression impossible');
    }
  };

  const hardRemove = async (id) => {
    if (!window.confirm('Supprimer définitivement ce produit ?')) return;
    try {
      await adminProductsAPI.hardDelete(id);
      await load();
    } catch (e) {
      setError('Suppression définitive impossible');
    }
  };

  const toggleAvailability = async (p) => {
    try {
      const updated = await adminProductsAPI.updateAvailability(p.id, !p.available);
      setProducts(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch (e) {
      setError('Mise à jour disponibilité impossible');
    }
  };

  const saveInlinePrice = async (p, value) => {
    try {
      const updated = await adminProductsAPI.updatePrice(p.id, value);
      setProducts(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch (e) {
      setError('Mise à jour du prix impossible');
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Gestion des Produits</h1>
        <Button onClick={load} className="bg-gray-200 text-gray-800">Recharger</Button>
      </div>

      {/* Quick selector to edit an existing product */}
      <div className="bg-white border rounded-xl p-4 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm text-gray-700 mb-1">Sélectionner un produit à modifier</label>
            <select
              className="w-full border rounded h-10 px-3"
              value={editingId || ''}
              onChange={(e) => {
                const id = e.target.value;
                setEditingId(id || null);
                const p = products.find(pr => pr.id === id);
                if (p) {
                  setForm({
                    ...p,
                    specs: (p.specs || []).join('\n'),
                    images: (p.images || []).join('\n'),
                  });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <option value="">— Choisir —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {editingId && (
            <Button type="button" className="bg-gray-200 text-gray-800" onClick={() => { setEditingId(null); setForm(emptyProduct); }}>Réinitialiser</Button>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="bg-white border rounded-xl p-6 shadow-md mb-10">
        <h2 className="text-lg font-semibold mb-4">{editingId ? 'Modifier le produit' : 'Ajouter un produit'}</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Nom</label>
            <Input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} required />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Catégorie</label>
            <select className="w-full border rounded h-10 px-3" value={form.category} onChange={(e) => setForm(f => ({...f, category: e.target.value}))}>
              <option value="battery">Batterie</option>
              <option value="inverter">Onduleur</option>
              <option value="kit">Kit</option>
            </select>
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Spécifications (une ligne par point)</label>
            <textarea
              className="w-full border rounded p-3 h-32"
              placeholder="• Capacité: 200Ah&#10;• Tension: 51.2V&#10;• Durée de vie: >6000 cycles"
              value={form.specs}
              onChange={(e) => setForm(f => ({...f, specs: e.target.value}))}
            />
          </div>

          {/* Upload main image */}
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Image principale</label>
            <div className="flex items-center gap-3 flex-wrap">
              <input type="file" accept="image/*" onChange={uploadMain} />
              <Button type="button" className="bg-gray-100 text-gray-800" onClick={() => openGallery('main')}>Sélectionner depuis la galerie</Button>
              <Input value={form.image} onChange={(e) => setForm(f => ({...f, image: e.target.value}))} placeholder="/api/uploads/2025/09/xxxx.png" />
            </div>
            {(form.image_base64 || form.image) && <img src={form.image_base64 || form.image} alt="preview" className="mt-2 h-24 object-contain border" />}
          </div>

          {/* Upload gallery */}
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Galerie</label>
            <div className="flex items-center gap-3 flex-wrap">
              <input type="file" multiple accept="image/*" onChange={uploadGallery} />
              <Button type="button" className="bg-gray-100 text-gray-800" onClick={() => openGallery('multi')}>Sélectionner depuis la galerie</Button>
            </div>
            <Textarea value={form.images} onChange={(e) => setForm(f => ({...f, images: e.target.value}))} rows={4} placeholder="/api/uploads/2025/09/a.png\n/api/uploads/2025/09/b.png" />
            {form.images_base64 && form.images_base64.length > 0 && (
              <div className="mt-2 grid grid-cols-4 gap-2">
                {form.images_base64.map((base64, idx) => (
                  <img key={idx} src={base64} alt={`Gallery ${idx+1}`} className="h-20 object-contain border" />
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Spécifications (une ligne par point)</label>
            <Textarea value={form.specs} onChange={(e) => setForm(f => ({...f, specs: e.target.value}))} rows={4} placeholder="Capacité: 200 Ah\nTension: 51,2 V\nGarantie: 10 ans" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} rows={3} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Prix</label>
            <Input value={form.price} onChange={(e) => setForm(f => ({...f, price: e.target.value}))} placeholder="ex: 1299€ TTC ou Sur devis" />
          </div>
          <div className="flex items-center space-x-2">
            <input id="available" type="checkbox" checked={!!form.available} onChange={(e) => setForm(f => ({...f, available: e.target.checked}))} />
            <label htmlFor="available" className="text-sm text-gray-700">Disponible</label>
          </div>
          <div className="md:col-span-2 flex gap-3 mt-2">
            <Button type="submit" className="bg-yellow-600 text-black">{editingId ? 'Enregistrer' : 'Ajouter'}</Button>
            {editingId && (
              <Button type="button" onClick={() => { setEditingId(null); setForm(emptyProduct); }} className="bg-gray-200 text-gray-800">Annuler</Button>
            )}
          </div>
        </form>
        {error && <div className="text-red-600 text-sm mt-3">{error}</div>}
      </div>

      {/* List */}
      <div className="bg-white border rounded-xl p-6 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Produits</h2>
          <Button onClick={load} className="bg-gray-200 text-gray-800">Recharger</Button>
        </div>
        {loading ? (
          <div>Chargement...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Nom</th>
                  <th className="py-2">Catégorie</th>
                  <th className="py-2">Prix (inline)</th>
                  <th className="py-2">Dispo</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="py-2">{p.name}</td>
                    <td className="py-2">{p.category}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <Input defaultValue={p.price} onBlur={(e) => saveInlinePrice(p, e.target.value)} className="h-8 w-40" />
                        <Button size="sm" className="bg-gray-100 text-gray-800" onClick={(e) => {
                          const td = e.currentTarget.closest('td');
                          const input = td?.querySelector('input');
                          if (input) saveInlinePrice(p, input.value);
                        }}>Enregistrer</Button>
                      </div>
                    </td>
                    <td className="py-2">
                      <label className="inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only" checked={!!p.available} onChange={() => toggleAvailability(p)} />
                        <div className={`w-10 h-5 rounded-full transition-colors ${p.available ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      </label>
                    </td>
                    <td className="py-2 flex gap-2 flex-wrap">
                      <Button size="sm" className="bg-gray-200 text-gray-800" onClick={() => startEdit(p)}>Modifier</Button>
                      {p.available ? (
                        <Button size="sm" className="bg-gray-300 text-gray-900" onClick={() => toggleAvailability(p)}>Désafficher</Button>
                      ) : (
                        <Button size="sm" className="bg-green-600 text-white" onClick={() => toggleAvailability(p)}>Afficher</Button>
                      )}
                      <Button size="sm" className="bg-red-800 text-white" onClick={() => hardRemove(p.id)}>Supprimer</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && (
              <div className="text-sm text-gray-500 mt-3">Aucun produit trouvé. Ajoutez un produit ou cliquez Recharger.</div>
            )}
          </div>
        )}
      </div>

      {/* Gallery modal */}
      {galleryOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Galerie d'images</h3>
              <Button className="bg-gray-200 text-gray-800" onClick={() => setGalleryOpen(false)}>Fermer</Button>
            </div>
            {loading ? (
              <div>Chargement...</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[60vh] overflow-auto">
                {galleryItems.map((it) => (
                  <button key={it.path} onClick={() => pickFromGallery(it.url)} className="border rounded hover:shadow p-2">
                    <img src={it.url} alt={it.path} className="w-full h-28 object-contain" />
                    <div className="text-[10px] text-gray-500 mt-1 break-all">{it.path}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;